/**
 * License Plate OCR Reader
 * Uses Tesseract.js for client-side plate recognition
 */

import Tesseract from 'tesseract.js'

export interface PlateOCRResult {
  text: string
  confidence: number
  boundingBox?: {
    x: number
    y: number
    width: number
    height: number
  }
}

/**
 * Clean up OCR text to extract plate-like patterns
 */
function extractPlateFromText(text: string): string {
  // Remove whitespace and convert to uppercase
  let cleaned = text.toUpperCase().replace(/\s+/g, '')

  // Common OCR mistakes for plates
  cleaned = cleaned
    .replace(/O/g, '0')  // O -> 0 for numbers context
    .replace(/I/g, '1')  // I -> 1
    .replace(/S/g, '5')  // S -> 5 sometimes
    .replace(/B/g, '8')  // B -> 8 sometimes

  // Extract alphanumeric sequences (typical plate format)
  const platePattern = /[A-Z0-9]{2,10}/g
  const matches = cleaned.match(platePattern)

  if (matches && matches.length > 0) {
    // Return the longest match (most likely the plate)
    return matches.reduce((a, b) => a.length >= b.length ? a : b)
  }

  return cleaned.slice(0, 10) // Fallback: first 10 chars
}

/**
 * Read license plate from an image
 */
export async function readPlateFromImage(
  imageSource: string | File | Blob,
  onProgress?: (progress: number) => void
): Promise<PlateOCRResult | null> {
  try {
    const result = await Tesseract.recognize(
      imageSource,
      'eng', // English for US plates
      {
        logger: (info) => {
          if (info.status === 'recognizing text' && onProgress) {
            onProgress(info.progress * 100)
          }
        },
      }
    )

    const rawText = result.data.text.trim()

    if (!rawText) {
      return null
    }

    const plateText = extractPlateFromText(rawText)

    // Find the word with highest confidence that looks like a plate
    let bestConfidence = 0
    let bestBox: PlateOCRResult['boundingBox'] = undefined

    // Access words array from the result data (with type assertion for Tesseract.js)
    const words = (result.data as { words?: Array<{
      text: string
      confidence: number
      bbox: { x0: number; y0: number; x1: number; y1: number }
    }> }).words

    if (words) {
      for (const word of words) {
        const wordText = word.text.toUpperCase().replace(/\s+/g, '')
        if (wordText.length >= 2 && wordText.length <= 10) {
          if (word.confidence > bestConfidence) {
            bestConfidence = word.confidence
            bestBox = {
              x: word.bbox.x0,
              y: word.bbox.y0,
              width: word.bbox.x1 - word.bbox.x0,
              height: word.bbox.y1 - word.bbox.y0,
            }
          }
        }
      }
    }

    return {
      text: plateText,
      confidence: bestConfidence > 0 ? bestConfidence / 100 : result.data.confidence / 100, // Normalize to 0-1
      boundingBox: bestBox,
    }
  } catch (error) {
    console.error('[OCR] Failed to read plate:', error)
    return null
  }
}

/**
 * Preprocess image for better OCR results
 * Returns a canvas with enhanced contrast
 */
export async function preprocessImageForOCR(
  imageSource: string | File | Blob
): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image()

    img.onload = () => {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')

      if (!ctx) {
        resolve(img.src)
        return
      }

      // Scale down large images for faster processing
      const maxSize = 1200
      let width = img.width
      let height = img.height

      if (width > maxSize || height > maxSize) {
        const ratio = Math.min(maxSize / width, maxSize / height)
        width = Math.round(width * ratio)
        height = Math.round(height * ratio)
      }

      canvas.width = width
      canvas.height = height

      // Draw image
      ctx.drawImage(img, 0, 0, width, height)

      // Apply grayscale and increase contrast
      const imageData = ctx.getImageData(0, 0, width, height)
      const data = imageData.data

      for (let i = 0; i < data.length; i += 4) {
        // Grayscale
        const gray = 0.299 * data[i]! + 0.587 * data[i + 1]! + 0.114 * data[i + 2]!

        // Increase contrast
        const contrast = 1.5
        const factor = (259 * (contrast * 255 + 255)) / (255 * (259 - contrast * 255))
        const newGray = Math.max(0, Math.min(255, factor * (gray - 128) + 128))

        data[i] = newGray
        data[i + 1] = newGray
        data[i + 2] = newGray
      }

      ctx.putImageData(imageData, 0, 0)

      resolve(canvas.toDataURL('image/jpeg', 0.9))
    }

    img.onerror = () => {
      reject(new Error('Failed to load image'))
    }

    if (typeof imageSource === 'string') {
      img.src = imageSource
    } else {
      img.src = URL.createObjectURL(imageSource)
    }
  })
}

/**
 * Read plate with preprocessing
 */
export async function readPlateWithPreprocess(
  imageSource: string | File | Blob,
  onProgress?: (progress: number) => void
): Promise<PlateOCRResult | null> {
  try {
    // Preprocess for 0-30% progress
    onProgress?.(0)
    const processedImage = await preprocessImageForOCR(imageSource)
    onProgress?.(30)

    // OCR for 30-100% progress
    const result = await readPlateFromImage(processedImage, (p) => {
      onProgress?.(30 + p * 0.7)
    })

    return result
  } catch (error) {
    console.error('[OCR] Failed with preprocessing:', error)
    // Fallback to direct OCR
    return readPlateFromImage(imageSource, onProgress)
  }
}
